<template>
  <div class="b-table">
    <div class="table-wrapper">
      <table
        class="table"
        :class="tableClasses"
      >
        <thead v-if="columns.length">
          <tr>
            <th
              v-for="column in columns"
              :key="column.field"
              :class="{'is-sortable': column.sortable, 'is-current-sort': isSortedBy(column)}"
              @click.stop="sort(column)"
            >
              <div
                class="th-wrap"
                :class="{'is-numeric': column.numeric, 'is-centered': column.centered}"
              >
                <slot
                  name="header"
                  :column="column"
                  :sort-field="sortField"
                  :sort-order="sortOrder"
                >
                  {{ column.label }}
                </slot>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in visibleRows"
            :key="index"
          >
            <td
              v-for="column in columns"
              :key="column.field"
              :class="[column.cellClass ? column.cellClass(row) : undefined, {
                'has-text-right': column.numeric && !column.centered,
                'has-text-centered': column.centered,
              }]"
              :data-label="column.label"
            >
              <slot
                name="cell"
                :column="column"
                :row="row"
              >
                {{ row[column.field] }}
              </slot>
            </td>
          </tr>
          <tr
            v-if="!visibleRows.length"
            class="is-empty"
          >
            <td :colspan="columns.length">
              <slot name="empty"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="paginated"
      class="top level"
    >
      <div class="level-left"/>
      <div class="level-right">
        <div class="level-item">
          <nav class="pagination is-simple">
            <a
              role="button"
              href="#"
              class="pagination-link pagination-previous"
              :disabled="!hasPrev || undefined"
              aria-label="Vorherige Seite"
              @click.prevent="changePage(currentPage - 1)"
            >
              <HfIcon icon="angle-left"/>
            </a>
            <a
              role="button"
              href="#"
              class="pagination-link pagination-next"
              :disabled="!hasNext || undefined"
              aria-label="Nächste Seite"
              @click.prevent="changePage(currentPage + 1)"
            >
              <HfIcon icon="angle-right"/>
            </a>
            <small class="info">{{ firstItem }}-{{ lastItem }} / {{ rows.length }}</small>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="C extends TableColumn">
  import {computed, ref} from 'vue';
  import HfIcon from '@/components/ui/HfIcon.vue';
  import {Row, SortOrder, TableColumn} from '@/components/ui/table/table.model';
  import {sortRows} from '@/components/ui/table/table-sort';

  /**
   * Plain Bulma table with client-side sorting and simple pagination.
   * Renders the same markup and classes as the Buefy 0.9 table did, so the existing styles still apply.
   */
  const props = withDefaults(defineProps<{
    rows: Row[];
    columns: C[];
    paginated?: boolean;
    perPage?: number;
    defaultSort?: string;
    defaultSortDirection?: SortOrder;
    narrowed?: boolean;
    striped?: boolean;
    hoverable?: boolean;
  }>(), {
    paginated: false,
    perPage: 20,
    defaultSort: undefined,
    defaultSortDirection: 'asc',
    narrowed: false,
    striped: false,
    hoverable: false,
  });

  const emit = defineEmits<{
    sort: [field: string, order: SortOrder];
    'page-change': [page: number];
  }>();

  const currentPage = defineModel<number>('currentPage', {default: 1});

  const sortField = ref(props.defaultSort);
  const sortOrder = ref<SortOrder>(props.defaultSortDirection);

  const sortedRows = computed(() => sortField.value ? sortRows(props.rows, sortField.value, sortOrder.value) : props.rows);

  const pageCount = computed(() => Math.ceil(props.rows.length / props.perPage));
  const hasPrev = computed(() => currentPage.value > 1);
  const hasNext = computed(() => currentPage.value < pageCount.value);
  const firstItem = computed(() => Math.max((currentPage.value - 1) * props.perPage + 1, 0));
  const lastItem = computed(() => Math.min(currentPage.value * props.perPage, props.rows.length));

  const visibleRows = computed(() => {
    if (!props.paginated || sortedRows.value.length <= props.perPage) {
      return sortedRows.value;
    }
    const start = (currentPage.value - 1) * props.perPage;
    return sortedRows.value.slice(start, start + props.perPage);
  });

  const tableClasses = computed(() => ({
    'is-striped': props.striped,
    'is-narrow': props.narrowed,
    'is-hoverable': props.hoverable && visibleRows.value.length > 0,
  }));

  function isSortedBy(column: C): boolean {
    return !!column.sortable && column.field === sortField.value;
  }

  function sort(column: C): void {
    if (!column.sortable) {
      return;
    }
    if (column.field === sortField.value) {
      sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
      sortField.value = column.field;
      sortOrder.value = props.defaultSortDirection;
    }
    emit('sort', column.field, sortOrder.value);
  }

  function changePage(page: number): void {
    if (page < 1 || page > pageCount.value) {
      return;
    }
    currentPage.value = page;
    emit('page-change', page);
  }
</script>
